/* @jsxImportSource @emotion/react */

import React, { useRef } from 'react'
import classNames from 'classnames'
import { useIntl } from 'react-intl'

import { ImgSanity, TooltipOnHover } from '@slite/components'

import { Section } from '../../components/Section'
import { SourceIconsRow } from '../../components/SourceIconsRow'
import { WebsiteText } from '../../components/WebsiteText'
import { SMALL_BREAKPOINT } from '../../utils/breakpoints'
import { getPricingFeatures, getPricingPlans } from './data'
import { dottedUnderlineStyle } from './styles'

const MOBILE = `@media (max-width: ${SMALL_BREAKPOINT - 1}px)`
// On mobile the four columns are crushed, so the table scrolls horizontally
// while keeping a readable min-width. Desktop is full width and unaffected.
const TABLE_MIN_WIDTH = 900
// Section applies a hardcoded 20px horizontal padding on mobile. A matching
// negative right margin lets the scrollable table bleed to the right edge.
const SECTION_MOBILE_PADDING = 20

function FeaturePlanItem({
  item,
  emphasisRed,
}: {
  item: boolean | string
  emphasisRed?: boolean
}) {
  if (typeof item === 'string') {
    if (emphasisRed) {
      return (
        <span
          style={{
            color: 'var(--danger-content)',
            fontWeight: 600,
          }}
        >
          {item}
        </span>
      )
    }
    return item
  }
  if (item === true) {
    return (
      <ImgSanity
        className="margin-right-8"
        alt="check"
        src="https://cdn.sanity.io/images/dnsgqii5/production/91ab0da8fad96eef5a7b5913b51f0c7d52aee15f-121x121.webp"
        width={12}
        height={12}
      />
    )
  }
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 10,
        height: 1,
        backgroundColor: 'var(--shade-30)',
        verticalAlign: 'middle',
      }}
    />
  )
}

export function SectionPricingFeatures({ isYearly }: { isYearly: boolean }) {
  const intl = useIntl()
  const plans = getPricingPlans(intl)
  const features = getPricingFeatures(intl)

  const headerInnerRef = useRef<HTMLDivElement>(null)
  const handleBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const inner = headerInnerRef.current
    if (inner) {
      inner.style.transform = `translateX(${-e.currentTarget.scrollLeft}px)`
    }
  }

  return (
    <Section background="moon-dust-lighter" containerVerticalPadding={20}>
      <div
        css={{
          position: 'sticky',
          top: 90,
          zIndex: 2,
          [MOBILE]: {
            overflow: 'hidden',
            marginRight: -SECTION_MOBILE_PADDING,
          },
        }}
      >
        <div
          ref={headerInnerRef}
          css={{
            [MOBILE]: {
              minWidth: TABLE_MIN_WIDTH + SECTION_MOBILE_PADDING,
              paddingRight: SECTION_MOBILE_PADDING,
            },
          }}
        >
          <div className="grid-12 grid-gap-16">
            <div className="grid-span-3" />
            {plans.map((plan, index) => {
              const price = isYearly ? plan.price?.yearly : plan.price?.monthly
              return (
                <div className="grid-span-3" key={index}>
                  <div
                    className={classNames(
                      'padding-16 radius-larger padding-16 margin-bottom-16',
                      'background-shade-100 border-small border-alpha-shade-40'
                    )}
                    style={{ zIndex: 1, position: 'relative' }}
                  >
                    <WebsiteText variant="smallTitle">{plan.title}</WebsiteText>
                    <div className="font-size-7 padding-top-4">
                      {plan.price === null ? (
                        <a
                          href={plan.action.href}
                          className="shade-00 flexbox-inline flexbox-align-center flexbox-column-gap-4"
                          style={{
                            borderBottom: '1px solid currentColor',
                            textDecoration: 'none',
                          }}
                        >
                          {intl.formatMessage({
                            id: 'pricing.features.contactus',
                            defaultMessage: 'Contact us',
                          })}
                          <span aria-hidden className="flexbox-no-shrink">
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                            >
                              <path
                                d="M1.5 8.5L8.5 1.5M8.5 1.5H5M8.5 1.5V4.5"
                                stroke="currentColor"
                                strokeWidth="1"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </a>
                      ) : (
                        intl.formatMessage(
                          {
                            id: 'pricing.features.priceperperiod',
                            defaultMessage: '{price} per member/month',
                          },
                          {
                            price: `$${price}`,
                          }
                        )
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <div
        onScroll={handleBodyScroll}
        css={{
          [MOBILE]: {
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            marginRight: -SECTION_MOBILE_PADDING,
          },
        }}
      >
        <div
          css={{
            [MOBILE]: {
              minWidth: TABLE_MIN_WIDTH + SECTION_MOBILE_PADDING,
              paddingRight: SECTION_MOBILE_PADDING,
            },
          }}
        >
          {features.map((feature, index) => (
            <div key={index} className="margin-top-40">
              <div className="shade-10 uppercase font-size-8 font-size-7-not-small font-weight-extrabold padding-vertical-16">
                {feature.title}
              </div>
              {feature.items.map((item, index) => (
                <div
                  className="border-top-small border-alpha-shade-45 font-size-6"
                  key={index}
                >
                  <div
                    className="grid-12 grid-gap-16 padding-vertical-12 radius-large"
                    css={{
                      opacity:
                        item.comingSoon && !item.titleRed && !item.standardRed
                          ? 0.4
                          : 1,
                      ':hover': {
                        backgroundColor: 'var(--shade-100)',
                        marginLeft: '-12px',
                        marginRight: '-12px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                      },
                    }}
                  >
                    <div
                      className="grid-span-3 font-weight-semibold"
                      style={{ textWrap: 'pretty' }}
                    >
                      {item.description ? (
                        <TooltipOnHover
                          positions={['top', 'left']}
                          openOnTouch
                          content={
                            <div className="font-size-7 line-height-copy text-align-left">
                              {item.description}
                            </div>
                          }
                          target={({ ref, props }) => (
                            <div
                              className="flexbox flexbox-align-center"
                              ref={ref}
                              {...props}
                              style={{
                                cursor: 'help',
                              }}
                            >
                              <span
                                style={{
                                  ...dottedUnderlineStyle,
                                  ...(item.titleRed
                                    ? { color: 'var(--danger-content)' }
                                    : {}),
                                }}
                              >
                                {item.title}
                              </span>
                            </div>
                          )}
                        />
                      ) : (
                        <span
                          style={
                            item.titleRed
                              ? { color: 'var(--danger-content)' }
                              : undefined
                          }
                        >
                          {item.title}
                        </span>
                      )}
                    </div>
                    <div
                      className="grid-span-3 padding-left-32"
                      style={item.mutedStandard ? { opacity: 0.4 } : undefined}
                    >
                      <FeaturePlanItem
                        item={item.standard}
                        emphasisRed={item.standardRed}
                      />
                      {item.standardSubtext &&
                        (item.standardSubtextTooltip ? (
                          <TooltipOnHover
                            positions={['top', 'left']}
                            openOnTouch
                            content={
                              <div className="font-size-7 line-height-copy text-align-left">
                                {item.standardSubtextTooltip}
                              </div>
                            }
                            target={({ ref, props }) => (
                              <div
                                ref={ref}
                                {...props}
                                className="shade-20 margin-top-4"
                                style={{
                                  fontSize: 'calc(var(--font-size-6) - 1pt)',
                                  cursor: 'help',
                                }}
                              >
                                <span style={dottedUnderlineStyle}>
                                  {item.standardSubtext}
                                </span>
                              </div>
                            )}
                          />
                        ) : (
                          <div
                            className="shade-20 margin-top-4"
                            style={{
                              fontSize: 'calc(var(--font-size-6) - 1pt)',
                            }}
                          >
                            {item.standardSubtext}
                          </div>
                        ))}
                    </div>
                    <div className="grid-span-3 padding-left-32">
                      {item.sourceIcons && item.premium === true ? (
                        <SourceIconsRow
                          sources={item.sourceIcons}
                          showPlus={!item.mutedStandard}
                        />
                      ) : (
                        <>
                          <FeaturePlanItem item={item.premium} />
                          {item.sourceIcons &&
                            typeof item.premium === 'string' && (
                              <SourceIconsRow sources={item.sourceIcons} />
                            )}
                        </>
                      )}
                    </div>
                    <div className="grid-span-3 padding-left-32">
                      {item.sourceIcons && item.enterprise === true ? (
                        <SourceIconsRow
                          sources={item.sourceIcons}
                          showPlus={!item.mutedStandard}
                        />
                      ) : (
                        <>
                          <FeaturePlanItem item={item.enterprise} />
                          {item.sourceIcons &&
                            typeof item.enterprise === 'string' && (
                              <SourceIconsRow sources={item.sourceIcons} />
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}
